import { Director, director, game, mat4, screen, _decorator } from 'cc';
import DOMElementGenerator from './DOMElementGenerator';

const { ccclass } = _decorator;

const MATRIX_4_TEMP = mat4();

/**
 * A class that syncs the transform properties and visibility of a Cocos node with a DOM element.
 */
@ccclass('BindDOMNode')
export default class BindDOMNode extends DOMElementGenerator {
    private _w = 0;
    private _h = 0;
    private _anchorX = 0;
    private _anchorY = 0;
    private _m00 = 0;
    private _m01 = 0;
    private _m04 = 0;
    private _m05 = 0;
    private _m12 = 0;
    private _m13 = 0;

    private get UICamera() {
        return director.root.batcher2D.getFirstRenderCamera(this.node);
    }

    public resetInEditor() {
        this.node.transform.setAnchorPoint(0, 0);
    }

    protected onEnable() {
        director.on(Director.EVENT_BEFORE_DRAW, this._updateTransformMatrix, this);
        this._updateTransformMatrix(true);
        this.element.style.visibility = 'visible';
        // visibility of iframe is not working in iOS device (fixed until iOS18)
        // https://github.com/WebKit/WebKit/pull/10037
        // to have correct visibility, we set opacity as fallback
        this.element.style.opacity = '1';
    }

    protected onDisable() {
        director.off(Director.EVENT_BEFORE_DRAW, this._updateTransformMatrix, this);
        this.element.style.visibility = 'hidden';
        this.element.style.opacity = '0';
    }

    protected update() {
        this._updateTransformMatrix();
    }

    protected applyPropertyOnElement(element: HTMLElement) {
        super.applyPropertyOnElement(element);
        // HACK copy from cc.WebView, anchor point is restricted to 0, 0
        this.node.transform.setAnchorPoint(0, 0);
        element.style.position = 'absolute';
        element.style.bottom = '0px';
        element.style.left = '0px';
        element.style.transformOrigin = '0px 100% 0px';
        element.style['-webkit-transform-origin'] = '0px 100% 0px';
        element.style.visibility = this.enabledInHierarchy ? 'visible' : 'hidden';
    }

    private _updateTransformMatrix(force = false) {
        if (this.element.style.visibility === 'hidden') {
            return;
        }

        const camera = this.UICamera;
        if (!camera) {
            return;
        }

        this.node.getWorldMatrix(MATRIX_4_TEMP);
        camera.update(true);
        camera.worldMatrixToScreen(MATRIX_4_TEMP, MATRIX_4_TEMP, screen.windowSize.width, screen.windowSize.height);

        const { width, height } = this.node.transform.contentSize;
        const { anchorX, anchorY } = this.node.transform;
        if (
            !force &&
            this._m00 === MATRIX_4_TEMP.m00 &&
            this._m01 === MATRIX_4_TEMP.m01 &&
            this._m04 === MATRIX_4_TEMP.m04 &&
            this._m05 === MATRIX_4_TEMP.m05 &&
            this._m12 === MATRIX_4_TEMP.m12 &&
            this._m13 === MATRIX_4_TEMP.m13 &&
            this._w === width &&
            this._h === height &&
            this._anchorX === anchorX &&
            this._anchorY === anchorY
        ) {
            return;
        }

        // update matrix cache
        this._m00 = MATRIX_4_TEMP.m00;
        this._m01 = MATRIX_4_TEMP.m01;
        this._m04 = MATRIX_4_TEMP.m04;
        this._m05 = MATRIX_4_TEMP.m05;
        this._m12 = MATRIX_4_TEMP.m12;
        this._m13 = MATRIX_4_TEMP.m13;
        this._w = width;
        this._h = height;
        this._anchorX = anchorX;
        this._anchorY = anchorY;

        const dpr = screen.devicePixelRatio;
        const scaleX = 1 / dpr;
        const scaleY = 1 / dpr;

        const container = game.container!;
        const sx = MATRIX_4_TEMP.m00 * scaleX;
        const b = MATRIX_4_TEMP.m01 * scaleY;
        const c = MATRIX_4_TEMP.m04 * scaleX;
        const sy = MATRIX_4_TEMP.m05 * scaleY;

        this.element.style.width = `${width}px`;
        this.element.style.height = `${height}px`;
        const w = this._w * scaleX;
        const h = this._h * scaleY;

        const appx = w * MATRIX_4_TEMP.m00 * anchorX;
        const appy = h * MATRIX_4_TEMP.m05 * anchorY;

        const offsetX = container && container.style.paddingLeft ? parseInt(container.style.paddingLeft, 10) : 0;
        const offsetY = container && container.style.paddingBottom ? parseInt(container.style.paddingBottom, 10) : 0;
        const tx = MATRIX_4_TEMP.m12 * scaleX - appx + offsetX;
        const ty = MATRIX_4_TEMP.m13 * scaleY - appy + offsetY;

        const matrix = `matrix(${sx},${-b},${-c},${sy},${tx},${-ty})`;
        this.element.style.transform = matrix;
        this.element.style['-webkit-transform'] = matrix;
    }
}
